import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  buildScheduledScanStatusRows,
  customerSafeScheduledRunNotice,
  scheduledScanDefinitionsHistoryPath,
  selectEarliestNextOccurrenceLabel,
  type ScheduledScanStatusRow,
  type ScheduledRunStatusTone,
} from '../../domain/safetyCases/workspaceScheduledScanStatus';
import { scheduledScansApi } from '../../services/scheduledScansApi';
import { SCAN_OCCURRENCE_LABEL } from '../../domain/safetyCases/safetyCasesWorkspacePresentationCopy';
import { resolveScheduledRunScanOutcome, scheduledRunCreatedScanHref } from '../../domain/safetyCases/scheduledRunScanOutcome';

export type ScheduledScanDefinitionSelection = {
  definitionId: string;
  definitionName: string;
};

type PanelState =
  | { kind: 'loading' }
  | { kind: 'hidden' }
  | { kind: 'empty' }
  | { kind: 'unavailable'; message: string }
  | { kind: 'ready'; rows: ScheduledScanStatusRow[] };

function chipColor(tone: ScheduledRunStatusTone): 'default' | 'success' | 'warning' | 'error' {
  return tone;
}

function statusChipLabel(row: ScheduledScanStatusRow): string {
  if (row.statusLabel) return row.statusLabel;
  if (!row.rawStatus) return 'No run yet';
  return 'Status unavailable';
}

export function ScheduledScanStatusPanel({
  siteId,
  canView = true,
  selectedDefinitionId = null,
  onSelectDefinition,
  onDefinitionScopeResolved,
  onNextScheduled,
  onStatusRows,
}: {
  siteId?: string | null;
  canView?: boolean;
  /** When set, highlights the matching schedule card (URL `scan_definition`). */
  selectedDefinitionId?: string | null;
  /**
   * Click a card to scope the Scans table to that definition.
   * Pass null to clear. Parent owns URL persistence.
   */
  onSelectDefinition?: (selection: ScheduledScanDefinitionSelection | null) => void;
  /** Resolve definition name when URL has id and status rows load. */
  onDefinitionScopeResolved?: (selection: ScheduledScanDefinitionSelection | null) => void;
  /** Earliest next scheduled occurrence label for Current / Upcoming empty copy. */
  onNextScheduled?: (label: string | null) => void;
  onStatusRows?: (rows: ScheduledScanStatusRow[]) => void;
}) {
  const navigate = useNavigate();
  const [state, setState] = useState<PanelState>({ kind: 'loading' });
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!onDefinitionScopeResolved) return;
    if (state.kind !== 'ready') return;
    if (!selectedDefinitionId) {
      onDefinitionScopeResolved(null);
      return;
    }
    const match = state.rows.find((row) => row.definitionId === selectedDefinitionId);
    onDefinitionScopeResolved(
      match
        ? { definitionId: match.definitionId, definitionName: match.definitionName }
        : null,
    );
  }, [onDefinitionScopeResolved, selectedDefinitionId, state]);

  useEffect(() => {
    if (!onNextScheduled) return;
    if (state.kind === 'ready') {
      onNextScheduled(selectEarliestNextOccurrenceLabel(state.rows));
      return;
    }
    if (state.kind !== 'loading') onNextScheduled(null);
  }, [onNextScheduled, state]);

  useEffect(() => {
    if (!onStatusRows) return;
    if (state.kind === 'ready') {
      onStatusRows(state.rows);
      return;
    }
    if (state.kind !== 'loading') onStatusRows([]);
  }, [onStatusRows, state]);

  useEffect(() => {
    let cancelled = false;
    const trimmedSite = String(siteId || '').trim();
    if (!trimmedSite || !canView) {
      setState({ kind: 'hidden' });
      return undefined;
    }

    setState({ kind: 'loading' });
    void (async () => {
      try {
        const summary = await scheduledScansApi.getSiteStatusSummary(trimmedSite, 100);
        if (!summary.items.length) {
          if (!cancelled) setState({ kind: 'empty' });
          return;
        }

        if (!cancelled) {
          setState({
            kind: 'ready',
            rows: buildScheduledScanStatusRows(summary.items),
          });
        }
      } catch (error: unknown) {
        const status = Number((error as { response?: { status?: number } })?.response?.status || 0);
        const detailRaw = (error as {
          response?: { data?: { detail?: unknown; message?: unknown } };
          message?: unknown;
        })?.response?.data?.detail
          ?? (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message
          ?? (error as { message?: unknown })?.message;
        const detail = String(detailRaw || '').trim();
        const safeDetail = customerSafeScheduledRunNotice(detail) || '';
        const message = status === 401
          ? 'Your session is not authorized to view Scheduled Scan status. Please sign in again.'
          : status === 403
            ? 'You do not have permission to view Scheduled Scan status.'
            : safeDetail
              ? `Scheduled Scan status is temporarily unavailable: ${safeDetail}`
              : 'Scheduled Scan status is temporarily unavailable.';
        if (!cancelled) setState({ kind: 'unavailable', message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canView, retryKey, siteId]);

  if (state.kind === 'hidden') return null;

  if (state.kind === 'loading') {
    return (
      <Stack spacing={1} data-testid="scheduled-scan-status-panel-loading" sx={{ py: 0.5 }}>
        <Skeleton height={28} width="40%" />
        <Skeleton height={48} />
      </Stack>
    );
  }

  const historyPath = scheduledScanDefinitionsHistoryPath(String(siteId || '').trim());

  if (state.kind === 'unavailable') {
    return (
      <Stack spacing={0.75} data-testid="scheduled-scan-status-unavailable">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Scheduled Scan status
        </Typography>
        <Alert
          severity="warning"
          action={(
            <Button color="inherit" size="small" onClick={() => setRetryKey((value) => value + 1)}>
              Retry
            </Button>
          )}
        >
          {state.message}
        </Alert>
      </Stack>
    );
  }

  if (state.kind === 'empty') {
    return (
      <Stack
        spacing={0.75}
        data-testid="scheduled-scan-status-panel"
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          px: 1.5,
          py: 1.25,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Scheduled Scan status
        </Typography>
        <Typography variant="body2" color="text.secondary" data-testid="scheduled-scan-status-empty">
          Scheduled Scans not configured
        </Typography>
        <Box>
          <Button
            size="small"
            onClick={() => navigate(historyPath)}
            data-testid="scheduled-scan-status-manage"
          >
            Manage recurring Safety Scans
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack
      spacing={1}
      data-testid="scheduled-scan-status-panel"
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        px: 1.5,
        py: 1.25,
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Scheduled Scan status
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Cadence and latest occurrence for scheduled Safety Scans.
        </Typography>
      </Box>

      {selectedDefinitionId ? (
        <Box>
          <Button
            size="small"
            onClick={() => onSelectDefinition?.(null)}
            data-testid="scheduled-scan-status-clear-scope"
          >
            Clear definition filter
          </Button>
        </Box>
      ) : null}

      <Stack spacing={1} data-testid="scheduled-scan-status-rows">
        {state.rows.map((row) => {
          const selected = Boolean(selectedDefinitionId) && selectedDefinitionId === row.definitionId;
          return (
          <Box
            key={`${row.definitionId}:${row.scheduleId}`}
            data-testid={`scheduled-scan-status-row-${row.definitionId}`}
            aria-pressed={selected}
            onClick={() => {
              if (!onSelectDefinition) return;
              if (selected) {
                onSelectDefinition(null);
                return;
              }
              onSelectDefinition({
                definitionId: row.definitionId,
                definitionName: row.definitionName,
              });
            }}
            sx={{
              display: 'grid',
              gap: 0.5,
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1.2fr) minmax(0, 1fr) auto' },
              alignItems: 'start',
              cursor: onSelectDefinition ? 'pointer' : 'default',
              borderRadius: 1,
              px: 0.75,
              py: 0.5,
              outline: selected ? '2px solid' : '1px solid transparent',
              outlineColor: selected ? 'primary.main' : 'transparent',
              bgcolor: selected ? 'action.selected' : 'transparent',
              '&:hover': onSelectDefinition ? { bgcolor: 'action.hover' } : undefined,
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {row.definitionName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {row.cadenceLabel}
              </Typography>
              {row.nextOccurrenceLabel ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  Next: {row.nextOccurrenceLabel}
                </Typography>
              ) : null}
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">
                {SCAN_OCCURRENCE_LABEL}
              </Typography>
              <Typography variant="body2">{row.occurrenceLabel}</Typography>
              {row.observationWindowLabel ? (
                <Typography variant="caption" color="text.secondary" display="block">
                  Window: {row.observationWindowLabel}
                </Typography>
              ) : null}
              {row.notice ? (
                <Typography
                  variant="caption"
                  color="warning.main"
                  display="block"
                  data-testid={`scheduled-scan-status-notice-${row.definitionId}`}
                >
                  {row.notice}
                </Typography>
              ) : null}
              {(() => {
                const outcome = resolveScheduledRunScanOutcome({
                  id: row.runId,
                  status: row.rawStatus,
                  terminal_reason_code: row.terminalReasonCode,
                  terminal_reason_message: row.terminalReasonMessage,
                  links: {
                    result_summary_id: row.resultSummaryId,
                    report_id: row.reportId,
                  },
                });
                if (!row.runId) return null;
                const openHref = outcome.scanCreated
                  ? scheduledRunCreatedScanHref({
                    siteId,
                    reportId: outcome.createdScanId,
                  })
                  : null;
                return (
                  <Box data-testid={`scheduled-scan-status-outcome-${row.definitionId}`} sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Safety Scan outcome
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {outcome.scanOutcomeLabel}
                    </Typography>
                    {outcome.scanOutcomeReason ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {outcome.scanOutcomeReason}
                      </Typography>
                    ) : null}
                    {openHref ? (
                      <Button
                        size="small"
                        sx={{ mt: 0.25, px: 0, minWidth: 0, textTransform: 'none' }}
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(openHref);
                        }}
                        data-testid={`scheduled-scan-status-open-${row.definitionId}`}
                      >
                        Open Safety Scan
                      </Button>
                    ) : null}
                  </Box>
                );
              })()}
            </Box>
            <Chip
              size="small"
              color={chipColor(row.statusTone)}
              variant={row.statusLabel === 'Completed' ? 'filled' : 'outlined'}
              label={statusChipLabel(row)}
              data-testid={`scheduled-scan-status-chip-${row.definitionId}`}
              data-status-dimension="scan_occurrence"
              title="Scan occurrence status"
            />
          </Box>
          );
        })}
      </Stack>

      <Box>
        <Button
          size="small"
          onClick={() => navigate(historyPath)}
          data-testid="scheduled-scan-status-history"
        >
          Open Safety Scan history
        </Button>
      </Box>
    </Stack>
  );
}
